import { MediaProcessingError } from './errors';
import type { MediaProcessingJobOptions } from './types';

type QueueJob<T> = { options: MediaProcessingJobOptions; run: () => Promise<T>; resolve: (value: T | PromiseLike<T>) => void; reject: (reason?: unknown) => void };

/** Images use a bounded pool; videos are exclusive to cap encoder memory. */
export class MediaProcessingScheduler {
  private readonly queue: QueueJob<unknown>[] = [];
  private runningImages = 0;
  private runningVideo = false;
  constructor(private readonly maxImageConcurrency = 2) {
    if (!Number.isSafeInteger(maxImageConcurrency) || maxImageConcurrency < 1) throw new Error('maxImageConcurrency must be a positive integer');
  }
  enqueue<T>(options: MediaProcessingJobOptions, run: () => Promise<T>): Promise<T> {
    if (options.signal?.aborted) return Promise.reject(new MediaProcessingError('cancelled', 'Media processing was cancelled'));
    return new Promise<T>((resolve, reject) => {
      const job: QueueJob<T> = { options, run, resolve, reject };
      this.queue.push(job as QueueJob<unknown>);
      const onAbort = () => { const index = this.queue.indexOf(job as QueueJob<unknown>); if (index >= 0) { this.queue.splice(index, 1); reject(new MediaProcessingError('cancelled', 'Media processing was cancelled')); } };
      options.signal?.addEventListener('abort', onAbort, { once: true });
      void this.drain();
    });
  }
  get pendingCount() { return this.queue.length; }
  private canStart(kind: MediaProcessingJobOptions['kind']) { return kind === 'video' ? !this.runningVideo && this.runningImages === 0 : !this.runningVideo && this.runningImages < this.maxImageConcurrency; }
  private async drain() {
    const index = this.queue.findIndex((job) => this.canStart(job.options.kind)); if (index < 0) return;
    const [job] = this.queue.splice(index, 1); if (!job) return;
    if (job.options.kind === 'video') this.runningVideo = true; else this.runningImages += 1;
    try { if (job.options.signal?.aborted) throw new MediaProcessingError('cancelled', 'Media processing was cancelled'); job.resolve(await job.run()); } catch (error) { job.reject(error); } finally { if (job.options.kind === 'video') this.runningVideo = false; else this.runningImages -= 1; void this.drain(); }
  }
}
